import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import SectionTitle from '../common/SectionTitle';
import { post } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';
import useSettings from '../../hooks/useSettings'; // Import nového hooku

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    termsAgreed: false,
    honeypot: '',
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });
  const language = getUserLanguage();
  const { settings, error: settingsError, loading } = useSettings(); // Načtení všech nastavení

  // Log error if settings fetch fails
  if (settingsError) {
    console.error('[ContactSection] Settings fetch error:', settingsError.message, settingsError.stack);
  }

  // Fallback hodnoty, pokud načítání selže
  const contactDetails = {
    address: settings.contact_address?.translations?.[language]?.setting_value || 'Liberecká 150, Česká Lípa, 470 01',
    phone: settings.contact_phone?.translations?.[language]?.setting_value || '+420 721 770 063',
    email: settings.contact_email?.translations?.[language]?.setting_value || 'info@norisknodrift.com',
  };

  const displaySocialMedia = {
    INSTAGRAM: settings.instagram_url?.translations?.[language]?.setting_value || 'https://instagram.com/norisknodrift',
    FACEBOOK: settings.facebook_url?.translations?.[language]?.setting_value || 'https://facebook.com/norisknodrift',
    YOUTUBE: settings.youtube_url?.translations?.[language]?.setting_value || 'https://www.youtube.com/@norisknodrift',
    TIKTOK: settings.tiktok_url?.translations?.[language]?.setting_value || 'https://tiktok.com/@norisknodrift_',
  };

  // Dynamické texty podle jazyka
  const texts = {
    cs: {
      badge: 'Kontakt',
      title: 'Spojte se',
      highlightedText: 's námi',
      description: 'Máte zájem o spolupráci, drift taxi nebo nás chcete pozvat na event? Kontaktujte nás!',
      contactDetailsTitle: 'Kontaktní údaje',
      addressLabel: 'Adresa',
      phoneLabel: 'Telefon',
      emailLabel: 'Email',
      openingHoursLabel: 'Otevírací doba',
      openingHours: 'Po-Pá: 9:00 - 17:00\nSo-Ne: Dle dohody',
      socialMediaLabel: 'Sociální sítě',
      formTitle: 'Napište nám',
      successMessage: 'Děkujeme za zprávu! Ozveme se vám co nejdříve.',
      errorRequiredFields: 'Všechna pole jsou povinná.',
      errorTerms: 'Musíte souhlasit se zpracováním osobních údajů a podmínkami.',
      errorSpam: 'Spam detekován.',
      errorSubmit: 'Odeslání zprávy se nezdařilo. Zkuste to prosím znovu.',
      nameLabel: 'Vaše jméno',
      subjectLabel: 'Předmět',
      messageLabel: 'Zpráva',
      termsLabel: 'Souhlasím se zpracováním osobních údajů a podmínkami',
      namePlaceholder: 'Jan Novák',
      emailPlaceholder: 'jan.novak@email.cz',
      subjectPlaceholder: 'Předmět zprávy',
      messagePlaceholder: 'Napište nám...',
      submittingText: 'Odesílání...',
      submitText: 'Odeslat zprávu',
    },
    en: {
      badge: 'Contact',
      title: 'Get in',
      highlightedText: 'touch',
      description: 'Interested in collaboration, drift taxi, or inviting us to an event? Contact us!',
      contactDetailsTitle: 'Contact Details',
      addressLabel: 'Address',
      phoneLabel: 'Phone',
      emailLabel: 'Email',
      openingHoursLabel: 'Opening Hours',
      openingHours: 'Mon-Fri: 9:00 AM - 5:00 PM\nSat-Sun: By appointment',
      socialMediaLabel: 'Social Media',
      formTitle: 'Write to Us',
      successMessage: 'Thank you for your message! We’ll get back to you soon.',
      errorRequiredFields: 'All fields are required.',
      errorTerms: 'You must agree to the processing of personal data and terms.',
      errorSpam: 'Spam detected.',
      errorSubmit: 'Failed to send the message. Please try again.',
      nameLabel: 'Your Name',
      subjectLabel: 'Subject',
      messageLabel: 'Message',
      termsLabel: 'I agree to the processing of personal data and terms',
      namePlaceholder: 'John Doe',
      emailPlaceholder: 'john.doe@email.com',
      subjectPlaceholder: 'Message Subject',
      messagePlaceholder: 'Write to us...',
      submittingText: 'Sending...',
      submitText: 'Send Message',
    },
  };

  const currentText = texts[language] || texts.en;

  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setFormStatus({
        submitting: false,
        success: false,
        error: currentText.errorRequiredFields,
      });
      return;
    }
    if (!formData.termsAgreed) {
      setFormStatus({
        submitting: false,
        success: false,
        error: currentText.errorTerms,
      });
      return;
    }
    if (formData.honeypot && formData.honeypot.trim() !== '') {
      setFormStatus({
        submitting: false,
        success: false,
        error: currentText.errorSpam,
      });
      return;
    }

    setFormStatus({
      submitting: true,
      success: false,
      error: null,
    });

    try {
      await post('/contact-messages', formData);

      setFormStatus({
        submitting: false,
        success: true,
        error: null,
      });

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        termsAgreed: false,
        honeypot: '',
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);

      setFormStatus({
        submitting: false,
        success: false,
        error: currentText.errorSubmit,
      });
    }
  };

  return (
    <section id="contact" className="py-20 bg-black" ref={sectionRef}>
      <div className="container mx-auto px-4 sm:px-6">
        <SectionTitle
          badge={currentText.badge}
          title={currentText.title}
          highlightedText={currentText.highlightedText}
          description={currentText.description}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Kontaktní údaje */}
          <div className={inView ? 'animate-slide-in-left' : 'opacity-0'}>
            <div className="drift-card p-8">
              <h3 className="text-2xl font-title font-bold text-white mb-6">{currentText.contactDetailsTitle}</h3>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-red-600/20 rounded flex items-center justify-center flex-shrink-0 mr-4">
                    <i className="fas fa-map-marker-alt text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{currentText.addressLabel}</h4>
                    <p className="text-gray-400 mt-1">{contactDetails.address}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-red-600/20 rounded flex items-center justify-center flex-shrink-0 mr-4">
                    <i className="fas fa-phone text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{currentText.phoneLabel}</h4>
                    <a
                      href={`tel:${contactDetails.phone.replace(/\s/g, '')}`}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      {contactDetails.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-red-600/20 rounded flex items-center justify-center flex-shrink-0 mr-4">
                    <i className="fas fa-envelope text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{currentText.emailLabel}</h4>
                    <a
                      href={`mailto:${contactDetails.email}`}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      {contactDetails.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-red-600/20 rounded flex items-center justify-center flex-shrink-0 mr-4">
                    <i className="fas fa-clock text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{currentText.openingHoursLabel}</h4>
                    {currentText.openingHours.split('\n').map((line, index) => (
                      <p key={index} className="text-gray-400 mt-1">{line}</p>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h4 className="text-white font-medium mb-4">{currentText.socialMediaLabel}</h4>
                <div className="flex space-x-3">
                  <a
                    href={displaySocialMedia.FACEBOOK}
                    className="w-10 h-10 bg-red-600/20 hover:bg-red-600 rounded flex items-center justify-center text-red-500 hover:text-white transition-colors"
                  >
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a
                    href={displaySocialMedia.INSTAGRAM}
                    className="w-10 h-10 bg-red-600/20 hover:bg-red-600 rounded flex items-center justify-center text-red-500 hover:text-white transition-colors"
                  >
                    <i className="fab fa-instagram"></i>
                  </a>
                  <a
                    href={displaySocialMedia.YOUTUBE}
                    className="w-10 h-10 bg-red-600/20 hover:bg-red-600 rounded flex items-center justify-center text-red-500 hover:text-white transition-colors"
                  >
                    <i className="fab fa-youtube"></i>
                  </a>
                  <a
                    href={displaySocialMedia.TIKTOK}
                    className="w-10 h-10 bg-red-600/20 hover:bg-red-600 rounded flex items-center justify-center text-red-500 hover:text-white transition-colors"
                  >
                    <i className="fab fa-tiktok"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Kontaktní formulář */}
          <div className={inView ? 'animate-slide-in-right' : 'opacity-0'}>
            <div className="drift-card">
              <form id="contactForm" className="p-8" onSubmit={handleSubmit}>
                <h3 className="text-2xl font-title font-bold text-white mb-6">{currentText.formTitle}</h3>

                {formStatus.success && (
                  <div className="bg-green-600/20 border border-green-600/30 rounded-md p-4 mb-6">
                    <p className="text-green-400 text-sm">{currentText.successMessage}</p>
                  </div>
                )}

                {formStatus.error && (
                  <div className="bg-red-600/20 border border-red-600/30 rounded-md p-4 mb-6">
                    <p className="text-red-400 text-sm">{formStatus.error}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-white text-sm font-medium mb-2">
                      {currentText.nameLabel}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full bg-black/50 border border-gray-700 rounded py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                      placeholder={currentText.namePlaceholder}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-white text-sm font-medium mb-2">
                      {currentText.emailLabel}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-black/50 border border-gray-700 rounded py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                      placeholder={currentText.emailPlaceholder}
                      required
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="subject" className="block text-white text-sm font-medium mb-2">
                    {currentText.subjectLabel}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full bg-black/50 border border-gray-700 rounded py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                    placeholder={currentText.subjectPlaceholder}
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-white text-sm font-medium mb-2">
                    {currentText.messageLabel}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full bg-black/50 border border-gray-700 rounded py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                    placeholder={currentText.messagePlaceholder}
                    required
                  ></textarea>
                </div>

                {/* Hidden honeypot field */}
                <div style={{ display: 'none' }}>
                  <label htmlFor="honeypot">Honeypot</label>
                  <input
                    type="text"
                    id="honeypot"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                  />
                </div>

                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="termsAgreed"
                      checked={formData.termsAgreed}
                      onChange={handleChange}
                      required
                      className="mr-2"
                    />
                    <span className="text-white text-sm">{currentText.termsLabel}</span>
                  </label>
                </div>

                <button type="submit" className="btn-drift" disabled={formStatus.submitting}>
                  {formStatus.submitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {currentText.submittingText}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane text-xs mr-2"></i>
                      {currentText.submitText}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;